import { AxiosError } from 'axios';
import { put, call, select, takeLatest } from 'redux-saga/effects';
import { selectors as clientSelectors } from './selectors';
import { types, Client, UpdateClientRequest, CreateClientRequest, DeleteClientRequest } from './types';
import { API } from 'services/api/base';
import { getErrorCode, getErrorMessage } from 'services/api/normalizer';

export function* getClientsAsync() {
  yield put({ type: types.GET_CLIENTS.PENDING, payload: true });

  try {
    const api = new API('clients');
    const response: Client[] = yield call(api.getAll);

    yield put({ type: types.GET_CLIENTS.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.GET_CLIENTS.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.GET_CLIENTS.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.GET_CLIENTS.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.GET_CLIENTS.PENDING, payload: false });
  }
}

export function* createClientAsync({ payload }: CreateClientRequest) {
  yield put({ type: types.CREATE_CLIENT.PENDING, payload: true });

  try {
    const api = new API(`clients/client`);
    yield call(api.create, payload);

    yield put({ type: types.CREATE_CLIENT.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.CREATE_CLIENT.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.CREATE_CLIENT.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.CREATE_CLIENT.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.CREATE_CLIENT.PENDING, payload: false });
  }
}

export function* updateClientAsync({ payload }: UpdateClientRequest) {
  yield put({ type: types.UPDATE_CLIENT.PENDING, payload: true });

  try {
    const api = new API(`clients/client`);
    const response: Client = yield call(api.update, payload);

    yield put({ type: types.UPDATE_CLIENT.SUCCESS, payload: response });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      const errMsg = getErrorMessage(err as AxiosError);
      yield put({ type: types.UPDATE_CLIENT.ERROR, payload: errMsg || 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.UPDATE_CLIENT.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.UPDATE_CLIENT.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.UPDATE_CLIENT.PENDING, payload: false });
  }
}

export function* deleteClientAsync({ payload }: DeleteClientRequest) {
  yield put({ type: types.DELETE_CLIENT.PENDING, payload: true });

  try {
    const { id } = payload;
    const api = new API(`clients/client`);
    yield call(api.destroy, id);

    yield put({ type: types.DELETE_CLIENT.SUCCESS });
  } catch (err) {
    const errorCode = getErrorCode(err as AxiosError) as number;

    if (errorCode >= 400 && errorCode < 500) {
      yield put({ type: types.DELETE_CLIENT.ERROR, payload: 'Invalid credentials' });
    } else if (errorCode >= 500) {
      yield put({ type: types.DELETE_CLIENT.ERROR, payload: 'Server error' });
    } else {
      yield put({ type: types.DELETE_CLIENT.ERROR, payload: 'Uh oh! Something went wrong. Please try again.' });
    }
  } finally {
    yield put({ type: types.DELETE_CLIENT.PENDING, payload: false });
  }
}

export function* invalidateCacheAsync() {
  yield put({ type: types.INVALIDATE_CACHE.PENDING, payload: true });

  try {
    const clientId: string = yield select(clientSelectors.selectClientId);
    const api = new API(`clients/${clientId}/cache`);
    yield call(api.destroy, '');

    yield put({ type: types.INVALIDATE_CACHE.SUCCESS });
  } catch (err) {
    yield put({ type: types.INVALIDATE_CACHE.ERROR, payload: 'Something went wrong refreshing the event data' });
  } finally {
    yield put({ type: types.INVALIDATE_CACHE.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.GET_CLIENTS.REQUEST, getClientsAsync),
  takeLatest(types.CREATE_CLIENT.REQUEST, createClientAsync),
  takeLatest(types.UPDATE_CLIENT.REQUEST, updateClientAsync),
  takeLatest(types.DELETE_CLIENT.REQUEST, deleteClientAsync),
  takeLatest(types.INVALIDATE_CACHE.REQUEST, invalidateCacheAsync)
];
